import type { TFunction } from "i18next";
import { Greeny, Neutrals, Premium, Primary, SemanticError, SemanticInfo, SemanticSuccess, Warning } from "../../../core-components/theme/color-palette/colors";
import { FileTypeFilter } from "../types";
import { SxProps, Theme } from '@mui/material';

export const getChipStylingBasedOnIcon = (fileTypeFilter: FileTypeFilter): SxProps<Theme> => {
  const {iconType, selected} = fileTypeFilter;
  let style: SxProps<Theme> = {
    color: "Primary.main",
    fontSize: '13px !important',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: '30px',
    paddingX: '3px',
    cursor: 'pointer',
    background: '#fff',
  };
  switch (iconType) {
    case 'image/':
      style = {
        ...style,
        background: selected ? Greeny[100] : '#fff',
        borderColor: Greeny[900]
      };
      break;
    case 'video/':
      style = {
        ...style,
        background: selected ? SemanticError[50] : '#fff',
        borderColor: SemanticError[600]
      };
      break;
    case 'audio/':
      style = {
        ...style,
        background: selected ? Premium[50] : '#fff',
       borderColor: Premium[900]
      };
      break;
    case 'application/pdf':
      style = {
        ...style,
        background: selected ? SemanticInfo[50] : '#fff',
        borderColor: SemanticInfo[600]
      };
      break;
    case 'application/msword':
      style = {
        ...style,
        background: selected ? Primary[100] : '#fff',
        borderColor: Primary[900]
      }
      break;
    case 'application/vnd.ms-excel':
    case 'text/csv':
      style = {
        ...style,
        background: selected ? SemanticSuccess[50] : '#fff',
        borderColor: SemanticSuccess[900]
      }
      break;
    case 'application/vnd.ms-powerpoint':
      style = {
        ...style,
        background: selected ? Warning[50] : '#fff',
        borderColor: Warning[600]
      }
      break;
    case 'text/plain':
      style = {
        ...style,
      background: selected ? Neutrals[100] : '#fff',
      borderColor: Neutrals[900]
    }
    break;
    default: 
      break;
    }
  return style;
}

export const PreDefinedFileTypeFilter: FileTypeFilter[] = [
  {
    label: 'Excel',
    iconType: 'application/vnd.ms-excel'
  },
  {
    label: 'PPT',
    iconType: 'application/vnd.ms-powerpoint'
  },
  {
    label: 'PDF',
    iconType: 'application/pdf'
  },
  {
    label: 'Image',
    iconType: 'image/'
  },
  {
    label: 'Video',
    iconType: 'video/'
  },
  {
    label: 'Audio',
    iconType: 'audio/'
  },
  {
    label: 'Word',
    iconType: 'application/msword'
  },
  {
    label: 'Text',
    iconType: 'text/plain'
  }
]

// Map raw label (used as API filter id) to a translated display label for UI chips.
export const getFileTypeFilterDisplayLabel = (label: string, t: TFunction): string => {
  switch (label) {
    case 'Excel':
      return t('fileManager.fileTypeLabels.excel', 'Excel');
    case 'PPT':
      return t('fileManager.fileTypeLabels.ppt', 'PPT');
    case 'PDF':
      return t('fileManager.fileTypeLabels.pdf', 'PDF');
    case 'Image':
      return t('fileManager.fileTypeLabels.image', 'Image');
    case 'Video':
      return t('fileManager.fileTypeLabels.video', 'Video');
    case 'Audio':
      return t('fileManager.fileTypeLabels.audio', 'Audio');
    case 'Word':
      return t('fileManager.fileTypeLabels.word', 'Word');
    case 'Text':
      return t('fileManager.fileTypeLabels.text', 'Text');
    default:
      return label;
  }
};

export const ALLOWED_MIME_TYPES: string[] = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'video/mp4',
  'video/webm',
  'video/avi',
  'video/mov',
  'video/mkv',
  'video/quicktime',
  'video/x-matroska',
  'video/webm',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
];

export const MAX_FILE_SIZE = 100 * 1024 * 1024;